"use client"

import { useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { isServerActionError } from "@/lib/with-query-handling"
import {
  getRunningCatalogSyncsAction,
  getShopifyInitialSyncStatusAction,
  type RunningCatalogSyncs,
  type StoreInitialSyncStatus,
  type SyncPartState,
} from "@/server-actions/shopify/sync-status"

export const shopifyInitialSyncQueryKey = ["shopify", "initial-sync"] as const
export const runningCatalogSyncsQueryKey = ["shopify", "catalog-sync-running"] as const

const POLL_INTERVAL_MS = 5000
const IDLE_POLL_INTERVAL_MS = 10000

function useRunningCatalogSyncsQuery() {
  return useQuery({
    queryKey: runningCatalogSyncsQueryKey,
    queryFn: async (): Promise<RunningCatalogSyncs> => {
      const result = await getRunningCatalogSyncsAction({})
      // `"status" in result`, not `isServerActionError` — see the note in billing-settings.ts.
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    // Slower rather than off when idle: a sync starts from the integrations page or from cron,
    // so nothing on this page would announce it.
    refetchInterval: (query) =>
      query.state.data?.integrationIds.length ? POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS,
  })
}

/** Whether a catalog export is in flight — reports re-syncs too, unlike `useInitialSyncPart`. */
export function useIsCatalogSyncRunning(integrationId?: string | null): boolean {
  const { data } = useRunningCatalogSyncsQuery()
  const ids = data?.integrationIds
  if (!ids?.length) return false
  return integrationId ? ids.includes(integrationId) : true
}

/**
 * Reload the catalog once a re-sync finishes. `useSyncCompletionRefresh` only covers the
 * first-load window, so without this the badge would clear over a stale product list.
 */
export function useCatalogSyncCompletionRefresh(
  refresh: () => void,
  integrationId?: string | null,
) {
  const running = useIsCatalogSyncRunning(integrationId)
  const wasRunning = useRef(running)

  useEffect(() => {
    if (wasRunning.current && !running) refresh()
    wasRunning.current = running
  }, [running, refresh])
}

export type InitialSyncPart = "orders" | "catalog"

/**
 * State of one part across the tenant's stores, or for a single one. Error wins over
 * progress so a broken store is never hidden behind another store's spinner.
 */
export function useInitialSyncPart(
  part: InitialSyncPart,
  integrationId?: string | null,
): SyncPartState | null {
  const { data: stores } = useShopifyInitialSyncQuery()
  if (!stores?.length) return null

  const scoped = integrationId ? stores.filter((s) => s.integrationId === integrationId) : stores
  const parts = scoped.map((s) => s[part])

  return parts.find((p) => p.status === "error")
    ?? parts.find((p) => p.status === "running" || p.status === "pending")
    ?? null
}

/**
 * Reload a list once a store finishes its first sync. The data hooks behind these tables are
 * not React Query, so the status poll cannot invalidate them — without this the table would
 * show its "nothing here" state at the exact moment the rows appeared.
 *
 * Tracks each integration separately rather than the aggregated display state: with one store
 * syncing and another in error, the aggregate stays `error` forever and the reload would never
 * fire. A store counts as finished when it reaches `done` or drops out of the payload —
 * running → error is a failure, not a completion.
 */
export function useSyncCompletionRefresh(
  part: InitialSyncPart,
  refresh: () => void,
  integrationId?: string | null,
) {
  const { data: stores } = useShopifyInitialSyncQuery()
  const activeIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    const scoped = (stores ?? []).filter(
      (s) => !integrationId || s.integrationId === integrationId,
    )
    const statusById = new Map(scoped.map((s) => [s.integrationId, s[part].status]))

    const finished = [...activeIds.current].some((id) => {
      const status = statusById.get(id)
      return status === undefined || status === "done"
    })

    activeIds.current = new Set(
      scoped
        .filter((s) => s[part].status === "running" || s[part].status === "pending")
        .map((s) => s.integrationId),
    )

    if (finished) refresh()
  }, [stores, part, integrationId, refresh])
}

/** Emitted by the integrations hook after a connect / disconnect / reinstall. */
const INTEGRATIONS_CHANGED_EVENT = "shopify-integrations-changed"

export function useShopifyInitialSyncQuery(enabled = true) {
  const queryClient = useQueryClient()

  // An empty result stops the polling and stays fresh for the client's stale window, so a
  // store connected in that window would never be picked up without this.
  useEffect(() => {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: shopifyInitialSyncQueryKey })
    }
    window.addEventListener(INTEGRATIONS_CHANGED_EVENT, invalidate)
    return () => window.removeEventListener(INTEGRATIONS_CHANGED_EVENT, invalidate)
  }, [queryClient])

  return useQuery<StoreInitialSyncStatus[]>({
    queryKey: shopifyInitialSyncQueryKey,
    enabled,
    queryFn: async () => {
      const result = await getShopifyInitialSyncStatusAction({})

      if (isServerActionError(result)) {
        throw new Error(result.errorMessage || "Failed to load Shopify sync status")
      }

      return result
    },
    // Poll only while something can still change; a finished or empty result stops it.
    refetchInterval: (query) => {
      const stores = query.state.data
      if (!stores?.length) return false
      const active = stores.some((s) =>
        [s.orders.status, s.catalog.status].some((st) => st === "running" || st === "pending"),
      )
      return active ? POLL_INTERVAL_MS : false
    },
  })
}
